import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProductoVariantApiService } from '../services/producto-variant-api.service';
import { ProductoVariantAttributeApiService } from '../services/producto-variant-attribute-api.service';
import {
  ProductVariantResponse,
  ProductVariantRequest,
} from '../models/producto-variant.model';
import { ProductVariantAttributeResponse } from '../models/producto-variant-attribute.model';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-producto-variants',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './producto-variants.component.html',
})
export class ProductoVariantsComponent implements OnInit, OnChanges {
  @Input() productId!: number;
  @Input() businessId!: number;
  @Input() attributes: ProductVariantAttributeResponse[] = [];
  @Input() currency?: string = undefined;

  variants: ProductVariantResponse[] = [];
  loading = false;
  error = '';

  // Modal state
  showModal = false;
  editingVariant: ProductVariantResponse | null = null;
  saving = false;

  // Form
  form: ProductVariantRequest = {
    sku: '',
    name: '',
    priceOverride: undefined,
    priceSurcharge: undefined,
    cost: undefined,
    stockQuantity: undefined,
    isActive: true,
    attributeValueIds: [],
    imageUrl: undefined,
  };

  // Inc 41: subida de imagen de variante
  uploadingImage = false;

  // Loaded attributes (if not passed via Input)
  localAttributes: ProductVariantAttributeResponse[] = [];

  constructor(
    private variantApi: ProductoVariantApiService,
    private variantAttrApi: ProductoVariantAttributeApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.cargarVariantes();
    if (this.attributes.length === 0) {
      this.cargarAtributos();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['attributes'] && this.attributes.length > 0) {
      this.localAttributes = this.attributes;
    }
  }

  get allAttributes(): ProductVariantAttributeResponse[] {
    return this.attributes.length > 0 ? this.attributes : this.localAttributes;
  }

  cargarVariantes(): void {
    this.loading = true;
    this.variantApi.getProductVariants(this.productId).subscribe({
      next: (data) => {
        this.variants = data;
        this.loading = false;
      },
      error: () => {
        this.variants = [];
        this.loading = false;
      },
    });
  }

  cargarAtributos(): void {
    this.variantAttrApi.getProductVariantAttributes(this.productId).subscribe({
      next: (data) => {
        this.localAttributes = data;
      },
      error: () => {
        this.localAttributes = [];
      },
    });
  }

  abrirCrear(): void {
    this.editingVariant = null;
    this.form = {
      sku: '',
      name: '',
      priceOverride: undefined,
      priceSurcharge: undefined,
      cost: undefined,
      stockQuantity: undefined,
      isActive: true,
      attributeValueIds: [],
      imageUrl: undefined,
    };
    this.showModal = true;
  }

  abrirEditar(variant: ProductVariantResponse): void {
    this.editingVariant = variant;
    this.form = {
      sku: variant.sku || '',
      name: variant.name || '',
      priceOverride: variant.priceOverride,
      priceSurcharge: variant.priceSurcharge,
      cost: variant.cost,
      stockQuantity: variant.stockQuantity,
      isActive: variant.isActive,
      attributeValueIds: variant.attributeValues?.map((v) => v.id) || [],
      imageUrl: variant.imageUrl || undefined,
    };
    this.showModal = true;
  }

  // Inc 41: subir imagen de la variante (guarda el publicUrl en form.imageUrl)
  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.toast.error({
        title: 'Formato no válido',
        message: 'Selecciona una imagen (JPG, PNG, WEBP).',
      });
      return;
    }
    this.uploadingImage = true;
    this.variantApi.uploadVariantImage(file).subscribe({
      next: (res) => {
        this.uploadingImage = false;
        this.form.imageUrl = res.publicUrl || undefined;
        if (!res.publicUrl) {
          this.toast.error({
            title: 'Aviso',
            message: 'La imagen se subió pero no se obtuvo una URL pública.',
          });
        }
      },
      error: (err) => {
        this.uploadingImage = false;
        this.toast.error({
          title: 'Error al subir',
          message: err.error?.message || 'No se pudo subir la imagen.',
        });
      },
    });
  }

  quitarImagen(): void {
    this.form.imageUrl = undefined;
  }

  cerrarModal(): void {
    this.showModal = false;
    this.editingVariant = null;
  }

  toggleAttributeValue(valueId: number): void {
    const ids = this.form.attributeValueIds || [];
    const idx = ids.indexOf(valueId);
    if (idx === -1) {
      this.form.attributeValueIds = [...ids, valueId];
    } else {
      this.form.attributeValueIds = ids.filter((id) => id !== valueId);
    }
  }

  isValueSelected(valueId: number): boolean {
    return (this.form.attributeValueIds || []).includes(valueId);
  }

  guardar(): void {
    this.saving = true;
    this.error = '';

    const request: ProductVariantRequest = {
      sku: this.form.sku || undefined,
      name: this.form.name || undefined,
      priceOverride: this.form.priceOverride ?? undefined,
      priceSurcharge: this.form.priceSurcharge ?? undefined,
      cost: this.form.cost ?? undefined,
      stockQuantity: this.form.stockQuantity ?? undefined,
      isActive: this.form.isActive,
      attributeValueIds: this.form.attributeValueIds?.length
        ? this.form.attributeValueIds
        : undefined,
      imageUrl: this.form.imageUrl || undefined,
    };

    if (this.editingVariant) {
      this.variantApi
        .updateProductVariant(
          this.productId,
          this.editingVariant.id,
          request,
          this.businessId,
        )
        .subscribe({
          next: () => {
            this.saving = false;
            this.cerrarModal();
            this.cargarVariantes();
            this.toast.updated('Variante');
          },
          error: (err) => {
            this.saving = false;
            this.error =
              err.error?.message || 'Error al actualizar la variante';
            this.toast.error({
              title: 'Error',
              message: err.error?.message || 'Error al actualizar la variante',
            });
          },
        });
    } else {
      this.variantApi
        .createProductVariant(this.productId, request, this.businessId)
        .subscribe({
          next: () => {
            this.saving = false;
            this.cerrarModal();
            this.cargarVariantes();
            this.toast.created('Variante');
          },
          error: (err) => {
            this.saving = false;
            this.error = err.error?.message || 'Error al crear la variante';
            this.toast.error({
              title: 'Error',
              message: err.error?.message || 'Error al crear la variante',
            });
          },
        });
    }
  }

  async eliminar(variant: ProductVariantResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar variante',
      message: `¿Eliminar la variante "${variant.sku}"?`,
    });
    if (!ok) return;
    this.variantApi
      .deleteProductVariant(this.productId, variant.id, this.businessId)
      .subscribe({
        next: () => {
          this.cargarVariantes();
          this.toast.deleted('Variante');
        },
        error: (err) => {
          this.error = err.error?.message || 'Error al eliminar la variante';
          this.toast.error({
            title: 'Error',
            message: err.error?.message || 'Error al eliminar la variante',
          });
        },
      });
  }

  formatPrecio(price?: number): string {
    if (price === undefined || price === null) return '-';
    return `${this.currency} ${price}`;
  }
}
